import { useState, useEffect, useCallback } from 'react'
import weatherService from '../services/weatherService'

// Custom hook for weather functionality
export const useWeather = () => {
  const [weatherData, setWeatherData] = useState(null)
  const [location, setLocation] = useState(null)
  const [forecast, setForecast] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [locationLoading, setLocationLoading] = useState(false)

  // Initialize weather data
  const initializeWeather = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get user location
      const userLocation = await weatherService.getUserLocation()
      if (!userLocation) throw new Error('Could not determine location')
      
      setLocation(userLocation)
      
      // Get current weather
      const weather = await weatherService.getCurrentWeather(userLocation)
      setWeatherData(weather)
      
      // Get forecast
      const forecastData = await weatherService.getForecast(userLocation)
      setForecast(forecastData)
      
    } catch (err) {
      setError(err.message)
      console.error('Weather initialization error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh weather data for current location
  const refreshWeather = useCallback(async () => {
    if (!location) return
    
    try {
      setError(null)
      
      const weather = await weatherService.getCurrentWeather(location)
      setWeatherData(weather)
      
      const forecastData = await weatherService.getForecast(location)
      setForecast(forecastData)
      
    } catch (err) {
      setError(err.message)
      console.error('Weather refresh error:', err)
    }
  }, [location])

  // Change location manually
  const changeLocation = useCallback(async (newLocation) => {
    try {
      setLocationLoading(true)
      setError(null)
      
      // Save new location preference
      weatherService.saveLocationPreference(newLocation)
      setLocation(newLocation)
      
      // Get weather for new location
      const weather = await weatherService.getCurrentWeather(newLocation)
      setWeatherData(weather)
      
      const forecastData = await weatherService.getForecast(newLocation)
      setForecast(forecastData)
      
    } catch (err) {
      setError(err.message)
      console.error('Location change error:', err)
    } finally {
      setLocationLoading(false)
    }
  }, [])

  // Search for cities
  const searchCities = useCallback(async (query) => {
    try {
      return await weatherService.searchCities(query)
    } catch (err) {
      console.error('City search error:', err)
      return []
    }
  }, [])

  // Request geolocation permission and update
  const requestLocation = useCallback(async () => {
    try {
      setLocationLoading(true)
      setError(null)
      
      const geoLocation = await weatherService.getGeolocation()
      if (geoLocation) {
        setLocation(geoLocation)
        
        const weather = await weatherService.getCurrentWeather(geoLocation)
        setWeatherData(weather)
        
        const forecastData = await weatherService.getForecast(geoLocation)
        setForecast(forecastData)
      }
      
    } catch (err) {
      setError(err.message)
      console.error('Geolocation request error:', err)
    } finally {
      setLocationLoading(false)
    }
  }, [])

  // Clear weather cache
  const clearCache = useCallback(() => {
    weatherService.clearCache()
    initializeWeather() // Reload after clearing cache
  }, [initializeWeather])

  // Initialize weather on mount
  useEffect(() => {
    initializeWeather()
  }, [initializeWeather])

  return {
    // Data
    weatherData,
    location,
    forecast,
    
    // States
    loading,
    error,
    locationLoading,
    
    // Actions
    refreshWeather,
    changeLocation,
    searchCities,
    requestLocation,
    clearCache,
    
    // Utility
    isDemo: weatherData?.isDemo || false
  }
}

// Simpler hook for just current weather (for widgets)
export const useCurrentWeather = () => {
  const [weather, setWeather] = useState(null)
  const [loading, setLoading] = useState(true)
  const [location, setLocation] = useState(null)
  
  const loadWeather = useCallback(async () => {
    try {
      setLoading(true)
      const userLocation = await weatherService.getUserLocation()
      if (userLocation) {
        setLocation(userLocation)
        const weatherData = await weatherService.getCurrentWeather(userLocation)
        setWeather(weatherData)
      }
    } catch (error) {
      console.error('Current weather error:', error)
    } finally {
      setLoading(false)
    }
  }, [])
  
  useEffect(() => {
    loadWeather()
    
    // Refresh every 10 minutes
    const interval = setInterval(loadWeather, 10 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])
  
  // Listen for location changes via custom events and localStorage
  useEffect(() => {
    const handleLocationChanged = (e) => {
      console.log('Weather widget: Location changed event received', e.detail)
      // Reload weather immediately when location changes
      loadWeather()
    }
    
    const handleStorageChange = (e) => {
      if (e.key === 'weather_location_preference') {
        console.log('Weather widget: Storage change detected')
        // Location preference changed, reload weather
        setTimeout(loadWeather, 100) // Small delay to ensure data is saved
      }
    }
    
    // Listen for custom location change events (immediate)
    window.addEventListener('weatherLocationChanged', handleLocationChanged)
    
    // Listen for storage changes (cross-tab support)
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('weatherLocationChanged', handleLocationChanged)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [loadWeather])
  
  return { weather, loading }
}

export default useWeather
